import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useRef } from 'react'
import { getLessonById, updateLessonProgress } from '@/hooks/useLesson'
import { getAllCourses } from '@/hooks/useCourses'
import { AnimatePresence, motion } from 'framer-motion'
import Button from '@/components/Button'
import DOMPurify from 'dompurify'
import { Link, useNavigate } from '@tanstack/react-router'
import { List } from 'lucide-react'
 

 
import holder11 from '@/assets/holder13.webp'
import { FaQuestionCircle } from 'react-icons/fa'
export const Route = createFileRoute('/_auth/lessons/$lessonId')({
  component: RouteComponent,
})

function cleanQuillHtml(html: string) {
  // Remove Quill UI spans
  html = html.replace(/<span class="ql-ui">.*?<\/span>/g, '');
  // Remove empty paragraphs
  html = html.replace(/<p>(\s|&nbsp;)*<\/p>/g, '');
  html = html.replace(/\[ALERT\](.*?)\[\/ALERT\]/gi, '<div class="red-alert">$1</div>');
  html = html.replace(/\[FACT\](.*?)\[\/FACT\]/gi, '<div class="callout-box">$1</div>');
  html = html.replace(/\[ICON:([a-z0-9_-]+)\]/gi, '<span style="margin-top:-3px;" data-gg-icon="$1"></span>');

  // Convert bracket tags to styled divs
  html = html.replace(/\[TIP\](.*?)\[\/TIP\]/gi, '<div class="tip-box">$1</div>');


  // Remove empty paragraphs (including those with <br>, NBSP, zero-width chars, and empty spans)
  if (typeof window !== 'undefined') {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    doc.body.querySelectorAll('p').forEach(p => {
      // Skip if paragraph contains obvious content nodes
      if (p.querySelector('img, video, iframe, input, button, audio, canvas, svg')) {
        return;
      }
      const normalizedInner = p.innerHTML
        .replace(/<span[^>]*>\s*<\/span>/gi, '')
        .replace(/<br\s*\/?>(\s|&nbsp;|\u00A0)*/gi, '')
        .replace(/[\u00A0\u200B\u200C\u200D]/g, '')
        .replace(/&nbsp;/gi, '')
        .trim();
      const textOnly = (p.textContent || '')
        .replace(/[\u00A0\u200B\u200C\u200D]/g, '')
        .trim();
      if (normalizedInner === '' && textOnly === '') {
        p.remove();
      }
    });
    html = doc.body.innerHTML;
  } else {
    // Fallback: broad regex removal when DOM not available
    html = html.replace(/<p>(?:\s|&nbsp;|\u00A0|<br\s*\/?>(?:\s|&nbsp;|\u00A0)*|<span[^>]*>(?:\s|&nbsp;|\u00A0|<br\s*\/?>(?:\s|&nbsp;|\u00A0)*)<\/span>)*<\/p>/gi, '');
  }
  
  return html;
}
function getAnswerLabel(index: number): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz';
  const letterIndex = index % 26;
  const cycle = Math.floor(index / 26);
  const letter = alphabet[letterIndex];
  return cycle === 0 ? `${letter}.` : `${cycle + 1}${letter}.`;
}

function getPageTitle(page: any): string {
  if (!page) return 'Page';
  const type = page.type;
  if (type === 'TEXT') return 'Reading';
  if (type === 'VIDEO') return page.videoPage?.title || 'Video';
  if (type === 'EMBED') return page.embedPage?.title || 'Embedded content';
  if (type === 'QUIZ') return page.quizPage?.question || 'Quick Check';
  return type || 'Page';
}

function LessonPagePreview({
  type,
  textContent,
  textImage,
  videoUrl,
  embedUrl,
  richContent,
  quizQuestion,
  quizAnswers,
  onSubmit,
  submitted,
  selected,
  setSelected,
}: {
  type: string;
  textContent?: string;
  textImage?: string;
  videoUrl?: string;
  embedUrl?: string;
  richContent?: string;
  quizQuestion?: string;
  quizAnswers?: { answer: string; correct: boolean; answerNumber: number }[];
  onSubmit?: () => void;
  submitted?: boolean;
  selected?: number[];
  setSelected?: (val: number[]) => void;
  feedback?: boolean[];
}) {
  // Video autoplay/unmute ergonomics for seniors: start muted, auto-unmute after first interaction
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [shouldUnmute, setShouldUnmute] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (type !== 'VIDEO') return;
    // Reset state when switching videos
    setShouldUnmute(false);
    setHasStarted(false);
  }, [type, videoUrl]);
  if (type === 'TEXT') {
    return (
      <div className="p-4 sm:p-8 md:p-10">
        {textImage && (
          <img src={textImage} alt="Lesson" className="mb-4 w-full h-full rounded" />
        )}
        <div className="prose prose-purple max-w-none quill-content text-bodytext text-base sm:text-lg md:text-xl font-normal text-slate-900 leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanQuillHtml(textContent || ''), { ADD_ATTR: ['class', 'data-list'] }) }} />
      </div>
    );
  }
  if (type === 'VIDEO') {
    return (
      <div className="p-0 sm:p-0">
        {videoUrl && (
          <div className="w-full aspect-video mb-4 relative">
            <video
              ref={videoRef}
              className="w-full h-full rounded"
              src={videoUrl}
              controls
              preload="metadata"
              playsInline
              muted={!shouldUnmute}
              onPlay={() => setHasStarted(true)}
            />
            {!hasStarted && (
              <button
                type="button"
                className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white rounded transition cursor-pointer bg-cover bg-center"
                style={{ backgroundImage: `url(${holder11})` }}
                onClick={() => {
                  try { sessionStorage.setItem('gg_has_interaction', '1'); } catch {}
                  setShouldUnmute(true);
                  const v = videoRef.current;
                  if (v) {
                    v.muted = false;
                    v.volume = 1;
                    v.play().catch(() => {});
                  }
                  setHasStarted(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const v = videoRef.current;
                    try { sessionStorage.setItem('gg_has_interaction', '1'); } catch {}
                    setShouldUnmute(true);
                    if (v) {
                      v.muted = false;
                      v.volume = 1;
                      v.play().catch(() => {});
                    }
                    setHasStarted(true);
                  }
                }}
                aria-label="Click to play video with sound"
              >
                <span className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-violet-700 text-violet-700 shadow-lg">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
                <span className="text-base sm:text-lg text-gray-700 font-medium">Click to play</span>
              </button>
            )}
          </div>
        )}
        {richContent && (
          <div className="prose prose-purple max-w-none quill-content text-bodytext text-base sm:text-lg text-gray-900 leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanQuillHtml(richContent), { ADD_ATTR: ['class', 'data-list'] }) }} />
        )}
      </div>
    );
  }
  if (type === 'EMBED') {
    return (
      <div className="p-4 sm:p-8 md:p-10">
        {embedUrl && (
          <div className="w-full aspect-video mb-4">
            <iframe
              className="w-full h-full rounded"
              src={embedUrl}
              title="Embedded content"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        )}
        {richContent && (
          <div className="prose prose-purple max-w-none quill-content text-bodytext text-base sm:text-lg text-gray-900 leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanQuillHtml(richContent), { ADD_ATTR: ['class', 'data-list'] }) }} />
        )}
      </div>
    );
  }
  if (type === 'QUIZ' && quizAnswers) {
    const isSelected = (idx: number) => selected?.includes(idx);
    const totalCorrect = quizAnswers.filter(a => a.correct).length;
    const correctSelected = selected ? selected.filter(idx => quizAnswers[idx]?.correct).length : 0;
    const selectedCount = selected ? selected.length : 0;
    const hasIncorrect = selected ? selected.some(idx => !quizAnswers[idx]?.correct) : false;
    const singleCorrect = totalCorrect === 1;
    return (
      <div className="p-4 sm:p-10 md:p-16">
        <div className="font-semibold mb-2">{quizQuestion}</div>
        <ul className="space-y-2 mb-4">
          {quizAnswers.map((a, i) => {
            const correct = a.correct;
            const showFeedback = submitted;
            let border = 'border-gray-300';
            let bg = 'bg-gray-50';
            let text = '';
            if (showFeedback) {
              if (isSelected(i) && correct) {
                border = 'border-green-400';
                bg = 'bg-green-50';
              } else if (isSelected(i) && !correct) {
                border = 'border-red-400';
                bg = 'bg-red-50';
              } else if (!isSelected(i) && correct) {
                border = 'border-green-200';
                bg = 'bg-green-50';
                text = 'text-green-700';
              }
            } else if (isSelected(i)) {
              border = 'border-violet-700';
              bg = 'bg-purple-50';
            }
            return (
              <li
                key={i}
                className={`p-2 sm:p-3 border rounded flex items-center gap-2 cursor-pointer transition ${border} ${bg} ${text}`}
                onClick={() => {
                  if (submitted) return;
                  if (!setSelected) return;
                  setSelected(isSelected(i) ? selected!.filter(idx => idx !== i) : [...selected!, i]);
                }}
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (submitted) return;
                    if (!setSelected) return;
                    setSelected(isSelected(i) ? selected!.filter(idx => idx !== i) : [...selected!, i]);
                  }
                }}
                aria-checked={isSelected(i)}
                role="checkbox"
              >
                <span className="font-bold mr-2 select-none" style={{ minWidth: 20, textAlign: 'left' }}>{getAnswerLabel(i)}</span>
                <span className="flex-1">{a.answer}</span>
                <span className={`w-5 h-5 flex items-center justify-center rounded-md border transition ${isSelected(i) ? 'border-purple-500 bg-purple-500' : 'border-gray-300 bg-white'}`}
                  style={{ minWidth: 20 }}
                >
                  {isSelected(i) && <span className="w-3 h-3 bg-white rounded" />}
                </span>
              </li>
            );
          })}
        </ul>
        <Button
          variant="primary"
          size="large"
          onClick={onSubmit}
          disabled={submitted || !selected || selected.length === 0}
        >
          Submit Answer{selected && selected.length > 1 ? 's' : ''}
        </Button>
        {submitted && (
          <div className="mt-4">
            {singleCorrect && selectedCount > 1 && hasIncorrect ? (
              <span className="font-semibold text-gray-700">
                Almost! You selected too many answers. Try to find the single best answer next time.
              </span>
            ) : (
              <span className="font-semibold text-gray-700">
                You got {correctSelected} out of {totalCorrect} correct.
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
  return null;
}

function RouteComponent() {
  const { lessonId } = Route.useParams();
  const searchParams = Route.useSearch();
  const invitationcode = searchParams.invitationcode;
  const navigate = useNavigate();
  const { data: lesson, isLoading, isError } = getLessonById(lessonId);
  const { data: courses } = getAllCourses();
  const updateProgress = updateLessonProgress();
  const [pageIdx, setPageIdx] = useState(0);
  const [selected, setSelected] = useState<number[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [feedback, setFeedback] = useState<boolean[] | undefined>(undefined);
  const [navDirection, setNavDirection] = useState(0);
  const [outlineOpen, setOutlineOpen] = useState(false);
  const [hasAwardedCompletion, setHasAwardedCompletion] = useState(false);

  const outlineButtonRef = useRef<HTMLButtonElement | null>(null);
  const outlineMenuRef = useRef<HTMLDivElement | null>(null);

  const [lastProgressPageId, setLastProgressPageId] = useState<string | null>(null);

  useEffect(() => {
    setSelected([]);
    setSubmitted(false);
    setFeedback(undefined);
  }, [pageIdx, lessonId]);

  // Initialize page index from user's progress
  useEffect(() => {
    if (lesson?.lessonProgress && lesson.lessonProgress.length > 0) {
      const userProgress = lesson.lessonProgress[0]; // Assuming first one is current user's
      if (userProgress && !userProgress.completed && lesson.pages) {
        // Use currentPageId to find the correct page
        if (userProgress.currentPageId) {
          const currentPageIndex = lesson.pages.findIndex((page: any) => page.id === userProgress.currentPageId);
          if (currentPageIndex >= 0) {
            setPageIdx(currentPageIndex);
          }
          // Set the last progress page to prevent immediate API call
          setLastProgressPageId(userProgress.currentPageId);
        }
      }
    }

  }, [lesson]);

  // Update progress only when user manually navigates to a different page
  const updatePageProgress = (newPageIdx: number) => {
    if (lesson?.pages && lesson.pages[newPageIdx]) {
      const currentPage = lesson.pages[newPageIdx];
      // Only update if this is a different page than what we last sent
      if (currentPage.id !== lastProgressPageId) {
        updateProgress.mutate({
          lessonId,
          currentPageId: currentPage.id,
        });
        setLastProgressPageId(currentPage.id);
      }
    }
    setNavDirection(newPageIdx > pageIdx ? 1 : -1);
    setPageIdx(newPageIdx);
  };

  // Keyboard navigation for previous/next using arrow keys
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lesson?.pages || lesson.pages.length === 0) return;
      const totalSlides = lesson.pages.length + 1; // includes completion slide
      if (e.key === 'ArrowLeft') {
        updatePageProgress(Math.max(0, pageIdx - 1));
      } else if (e.key === 'ArrowRight') {
        updatePageProgress(Math.min(totalSlides - 1, pageIdx + 1));
      } else if (e.key === 'Escape') {
        setOutlineOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lesson, pageIdx]);

  // Close outline on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!outlineOpen) return;
      const menu = outlineMenuRef.current;
      const btn = outlineButtonRef.current;
      const target = e.target as Node;
      if (menu && menu.contains(target)) return;
      if (btn && btn.contains(target)) return;
      setOutlineOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [outlineOpen]);

  // Auto-award completion when arriving at the final slide for the first time
  useEffect(() => {
    if (!lesson?.pages) return;
    const isOnCompletionSlide = pageIdx === lesson.pages.length;
    if (!isOnCompletionSlide) return;
    const userProgress = lesson.lessonProgress?.[0];
    const alreadyCompleted = Boolean(userProgress?.completed);
    if (alreadyCompleted || hasAwardedCompletion) return;
    if (lesson.pages.length > 0) {
      updateProgress.mutate({
        lessonId,
        completed: true,
        currentPageId: lesson.pages[lesson.pages.length - 1].id,
      });
    } else {
      updateProgress.mutate({
        lessonId,
        completed: true,
      } as any);
    }
    setHasAwardedCompletion(true);
  }, [pageIdx, lesson]);

  if (isLoading) return <div className="flex items-center justify-center h-screen max-w-5xl mx-auto">Loading...</div>;
  if (isError) {
    return <div>Error loading lesson.</div>;
  }
  if ((lesson as any)?.__forbidden) {
    navigate({ to: '/', replace: true, search: { invitationcode, __clerk_ticket: undefined } });
    return null;
  }
  if (!lesson) return <div>Lesson not found.</div>;
  if (!lesson.pages || lesson.pages.length === 0) return <div>No pages in this lesson.</div>;

  const totalSlides = lesson.pages.length + 1; // append a virtual completion slide
  const isCompletionSlide = pageIdx === lesson.pages.length;
  const page = !isCompletionSlide ? lesson.pages[pageIdx] : undefined;
  const activeCourse = courses?.find((c: any) => c.lessons?.some((l: any) => l.id === lesson.id));
  const chapterIndex = activeCourse?.lessons?.findIndex((l: any) => l.id === lesson.id) ?? -1;
  const chapterNumber = chapterIndex >= 0 ? chapterIndex + 1 : null;
  let previewProps: any = isCompletionSlide ? {} : { type: page.type };
  if (!isCompletionSlide && page.type === 'TEXT' && page.textPage) {
    previewProps.textContent = page.textPage.content;
    previewProps.textImage = page.textPage.image;
  } else if (!isCompletionSlide && page.type === 'VIDEO' && page.videoPage) {
    previewProps.videoUrl = page.videoPage.file;
    previewProps.richContent = page.videoPage.content;
  } else if (!isCompletionSlide && page.type === 'EMBED' && page.embedPage) {
    previewProps.embedUrl = page.embedPage.url;
    previewProps.richContent = page.embedPage.content;
  } else if (!isCompletionSlide && page.type === 'QUIZ' && page.quizPage) {
    previewProps.quizQuestion = page.quizPage.question;
    previewProps.quizAnswers = page.quizPage.answers;
    previewProps.selected = selected;
    previewProps.setSelected = setSelected;
    previewProps.submitted = submitted;
    previewProps.onSubmit = () => {
      if (!page.quizPage) return;
      const corrects = page.quizPage.answers.map((a: { correct: any; }, i: number) => a.correct === selected.includes(i));
      setFeedback(corrects);
      setSubmitted(true);
    };
    previewProps.feedback = feedback;
  }

  // Responsive container sizing handled via Tailwind classes below

  // progress handled via stepper

  return (
    <div className="flex flex-col items-center justify-start pb-10 min-h-dvh mx-auto w-full">

      <div className="w-full max-w-[1100px] px-4 sm:px-6">
        {/* Header */}
        <div className="sticky top-0 z-20 flex items-center justify-between bg-white/95 backdrop-blur rounded-2xl w-full mb-3">
          <div className="flex items-center gap-3 sm:gap-4">
            {activeCourse && (
              <Link
                to="/courses/$courseId"
                params={{ courseId: activeCourse.id }}
                search={{ invitationcode, __clerk_ticket: undefined }}
                className="px-3 py-2.5 rounded-md  text-gray-700 border border-violet-200 hover:bg-violet-200 transition-colors text-sm font-medium"
              >
                ← Back
              </Link>
            )}
            <div className="leading-tight">
              <div className="text-[11px] sm:text-sm text-gray-500"> {activeCourse ? (
                <Link
                  to="/courses/$courseId"
                  params={{ courseId: activeCourse.id }}
                  search={{ invitationcode, __clerk_ticket: undefined }}
                  className=" decoration-slate-400 text-slate-700 hover:text-violet-600 hover:decoration-violet-400 transition-colors text-xs uppercase tracking-wide text-gray-500"
                >
                  {` ${activeCourse.name ?? ''}`}
                </Link>
              ) : `${chapterNumber ?? ''}`}</div>
                
              <div className="text-base sm:text-lg font-semibold text-gray-800">{lesson.name}</div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <div className="relative">
              <Button
                variant="outline-black"
                size="medium"
                className="text-sm font-medium text-slate-700 focus:ring-0"
                onClick={() => setOutlineOpen(v => !v)}
                ref={outlineButtonRef as React.Ref<HTMLButtonElement>}
                aria-expanded={outlineOpen}
                aria-haspopup="menu"
              >
                <List size={16} className="mr-2" /> Outline
              </Button>
              <AnimatePresence>
                {outlineOpen && (
                  <motion.aside
                    ref={outlineMenuRef}
                    className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-200 p-2 z-40"
                    role="menu"
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 24 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="px-3 pt-2 pb-3 text-xs font-semibold text-gray-500 tracking-wide">CHAPTER OUTLINE</div>
                    <ul className="space-y-1 max-h-[60vh] overflow-auto pr-1">
                      {lesson.pages?.map((p: any, i: number) => {
                        const isActive = i === pageIdx;
                        return (
                          <li key={p.id || i}>
                            <button
                              className={`w-full text-left px-3 py-2 rounded-lg cursor-pointer text-sm flex items-center justify-between transition ${isActive ? 'bg-violet-50 text-violet-600 font-medium' : 'hover:bg-gray-50 text-slate-700'}`}
                              onClick={() => {
                                setOutlineOpen(false);
                                updatePageProgress(i);
                              }}
                            >
                              <span className="flex items-center gap-0">
                                <span className={`w-6 h-6 flex items-center justify-center text-sm ${isActive ? 'text-violet-600' : 'text-slate-600'}`}>{i + 1}.</span>
                                <span className="truncate text-sm max-w-[220px]">{getPageTitle(p)}</span>
                              </span>
                              {/* Optional duration placeholder */}
                              <span className="text-xs text-gray-400">{p.type === 'QUIZ' ? '1m' : ''}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </motion.aside>
                )}
              </AnimatePresence>
            </div>
          
            <Button variant="outline-black" size="medium" className="text-sm font-medium text-slate-700 " href="https://app.granguard.org/contact-support">
              <FaQuestionCircle className="mr-1 h-4 w-4" /> Help
            </Button>
          </div>
        </div>

        {/* Compact progress summary moved inside content card */}
        <div
          className="flex flex-col bg-white rounded-2xl border border-gray-100 w-full min-h-[60vh] pt-0 relative"
        >
          <div className="w-full mb-3 bg-gray-50 px-4 sm:px-6 py-3 bg-slate-50/70">
            <div className="flex items-center justify-between">
              <div className="text-[11px] sm:text-xs text-gray-600 tracking-wide uppercase">PROGRESS • {Math.min(pageIdx + 1, totalSlides)}/{totalSlides}</div>
              <div className="text-[11px] sm:text-xs text-gray-500">~{(lesson as any)?.duration ? `${(lesson as any).duration}m` : `${Math.max(1, Math.round(lesson.pages.length * 2))}m`}</div>
            </div>
            <div className="flex items-center gap-1 mt-1">
              {[...Array(totalSlides)].map((_, i: number) => (
                <span
                  key={i}
                  className={`h-1.5 w-6 sm:w-7 rounded-full ${i <= pageIdx ? 'bg-violet-500' : 'bg-gray-200'}`}
                  aria-hidden="true"
                />
              ))}
            </div>
          </div>
          <div className="text-[1.4rem] w-full flex-1 relative overflow-hidden">
            <AnimatePresence mode="wait" custom={navDirection}>
              <motion.div
                key={isCompletionSlide ? 'completion' : page.id}
                custom={navDirection}
                initial="enter"
                animate="center"
                exit="exit"
                variants={{
                  enter: (d: number) => ({ x: d > 0 ? 40 : -40, opacity: 0, position: 'absolute', width: '100%' }),
                  center: { x: 0, opacity: 1, position: 'relative', width: '100%' },
                  exit: (d: number) => ({ x: d > 0 ? -40 : 40, opacity: 0, position: 'absolute', width: '100%' }),
                }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
              >
                {isCompletionSlide ? (
                  <div className="p-6 sm:p-10 flex flex-col items-center justify-center text-center min-h-[40vh]">
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.35, ease: 'easeOut' }}
                    >
                      <svg width="160" height="160" viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="45" cy="45" r="35" fill="none" stroke="#e3e1ff" strokeWidth="1.5"/>
                        <path d="M30 47l12 12 18-22" stroke="#6c00ff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </motion.div>
                    <motion.div
                      className="text-xl font-base text-slate-700 mt-1"
                      initial={{ y: 8, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.15, duration: 0.3 }}
                    >
                      Great work! You’ve completed the chapter.
                    </motion.div>
                    <motion.div
                      className="mt-6 flex gap-4 flex-wrap items-center justify-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.25, duration: 0.3 }}
                    >
                      {activeCourse && (
                        <Link
                          to="/courses/$courseId"
                          params={{ courseId: activeCourse.id }}
                          search={{ invitationcode, __clerk_ticket: undefined }}
                        >
                          <Button variant="outline-black" size="large" className="border-violet-700">Back to Course</Button>
                        </Link>
                      )}
                      {(() => {
                        const sortedLessons = (activeCourse?.lessons ?? [])
                          .slice()
                          .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
                        const currentIdx = sortedLessons.findIndex((l: any) => l.id === lesson.id);
                        const afterCurrent = currentIdx >= 0 ? sortedLessons.slice(currentIdx + 1) : sortedLessons;
                        const nextIncompleteAfter = afterCurrent.find((l: any) => !(l.lessonProgress?.some((p: any) => p.completed)));
                        const firstIncompleteAnywhere = sortedLessons.find((l: any) => !(l.lessonProgress?.some((p: any) => p.completed)));
                        const sequentialNext = currentIdx >= 0 ? sortedLessons[currentIdx + 1] : undefined;
                        const targetNext = nextIncompleteAfter || firstIncompleteAnywhere || sequentialNext;
                        if (targetNext) {
                          return (
                            <Link
                              to="/lessons/$lessonId"
                              params={{ lessonId: targetNext.id }}
                              search={{ invitationcode, __clerk_ticket: undefined }}
                            >
                              <Button variant="primary" size="large">Next Chapter →</Button>
                            </Link>
                          );
                        }
                        return (
                          <Link
                            to="/learn"
                            search={{ tab: 'Courses', tour: searchParams.tour, invitationcode, __clerk_ticket: undefined }}
                          >
                            <Button variant="primary" size="large">Next Chapter →</Button>
                          </Link>
                        );
                      })()}
                    </motion.div>
                  </div>
                ) : (
                  <LessonPagePreview {...previewProps} />
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* In-card navigation controls */}
          <div className="mt-0 w-full flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-0 border-t border-gray-200 pt-4 pb-4 px-4">
            <Button
              variant="outline-black"
              className="shadow-sm w-full sm:w-auto md:min-w-[200px]"
              size="large"
              onClick={() => updatePageProgress(Math.max(0, pageIdx - 1))}
              disabled={pageIdx === 0}
              aria-label="Previous page"
            >
              &#8592; Previous Page
            </Button>
            <div className="text-sm text-gray-500 hidden sm:block">Tip: Use ← and → to move between slides</div>
            <Button
              variant="primary"
              size="large"
              className="shadow-lg w-full sm:w-auto md:min-w-[200px]"
              onClick={() => updatePageProgress(Math.min(totalSlides - 1, pageIdx + 1))}
              disabled={pageIdx === totalSlides - 1}
              aria-label="Next page"
            >
              Next Page &#8594;
            </Button>
          </div>
        </div>
      </div>
      {/* quill styles moved to App.css */}
    </div>
  );
}
